import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { memoBodyPlainText } from './memoBody';
import type { ApplicationSubmission, InternalMemoFormData } from '../types/applicationForm';
import { EMPTY_INTERNAL_MEMO_FORM } from '../types/applicationForm';

const TABLE = 'memo_submissions';
const FORM_SLUG = 'internal-memo' as const;

function withMemoSlug(row: ApplicationSubmission): ApplicationSubmission {
  return {
    ...row,
    form_slug: FORM_SLUG,
    form_data: normalizeInternalMemoFormData(row.form_data),
  };
}

export function normalizeInternalMemoFormData(raw: unknown): InternalMemoFormData {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<InternalMemoFormData>;
  const to = String(data.to ?? '').trim();
  // Salutation UI removed — Word greeting uses To; keep field synced for stored payloads.
  const salutation = to.replace(/^dear\s+/i, '').replace(/,+\s*$/, '').trim();
  // From UI removed — keep from_line synced with Issued by.
  const issued_by = String(data.issued_by ?? '').trim() || String(data.from_line ?? '').trim();
  const from_line = issued_by;
  return {
    ...EMPTY_INTERNAL_MEMO_FORM,
    ...data,
    ref_no: String(data.ref_no ?? ''),
    to,
    cc: String(data.cc ?? ''),
    from_line,
    date: String(data.date ?? ''),
    subject: String(data.subject ?? ''),
    salutation,
    body: String(data.body ?? ''),
    issued_by,
    acknowledged_by: '',
    signature_copy_name: data.signature_copy_name ? String(data.signature_copy_name) : undefined,
    signature_copy_path: data.signature_copy_path ? String(data.signature_copy_path) : undefined,
    posted_at: data.posted_at ? String(data.posted_at) : undefined,
    posted_by: data.posted_by ? String(data.posted_by) : undefined,
    cancelled_at: data.cancelled_at ? String(data.cancelled_at) : undefined,
    cancelled_by: data.cancelled_by ? String(data.cancelled_by) : undefined,
  };
}

export function subjectFromMemo(form: InternalMemoFormData): string {
  return form.subject.trim() || 'Internal Memorandum';
}

export async function fetchMemoSubmissions(): Promise<ApplicationSubmission[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('*').order('submitted_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => withMemoSlug(row as ApplicationSubmission));
}

export async function fetchMemoSubmission(id: string): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Submission not found.');
  return withMemoSlug(data as ApplicationSubmission);
}

export async function submitMemo(
  formData: InternalMemoFormData,
  submittedBy?: string,
  submitterEmail?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizeInternalMemoFormData(formData);
  const { data, error } = await db.rpc('submit_memo', {
    p_company_name: subjectFromMemo(payload),
    p_form_data: payload,
    p_submitted_by: submittedBy?.trim() || null,
    p_submitter_email: submitterEmail?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withMemoSlug(data as ApplicationSubmission);
}

export async function updateMemo(
  id: string,
  formData: InternalMemoFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizeInternalMemoFormData(formData);
  const { data, error } = await db.rpc('update_memo', {
    p_id: id,
    p_company_name: subjectFromMemo(payload),
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withMemoSlug(data as ApplicationSubmission);
}

export async function uploadMemoSignatureCopy(
  memoId: string,
  file: File,
): Promise<{ path: string; fileName: string }> {
  const db = getDbSupabase('forms');
  const safeName = file.name.replace(/[^\w.\-()+ ]+/g, '_').slice(0, 120) || 'signature';
  const path = `${memoId}/${Date.now()}_${safeName}`;
  const { error } = await db.storage.from('memo-signatures').upload(path, file, {
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(error.message || 'Failed to upload signature copy.');
  return { path, fileName: file.name };
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function blobToBase64(blob: Blob): Promise<string> {
  return bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
}

async function signedCopyBlobToPdfBase64(blob: Blob, fileName: string): Promise<string> {
  const type = blob.type.toLowerCase();
  const lowerName = fileName.toLowerCase();

  if (type === 'application/pdf' || lowerName.endsWith('.pdf')) {
    return blobToBase64(blob);
  }

  if (type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(lowerName)) {
    const { PDFDocument } = await import('pdf-lib');
    const pdf = await PDFDocument.create();
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let image;
    if (type === 'image/png' || lowerName.endsWith('.png')) {
      image = await pdf.embedPng(bytes);
    } else if (type === 'image/jpeg' || /\.jpe?g$/i.test(lowerName)) {
      image = await pdf.embedJpg(bytes);
    } else {
      const bmp = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not read signed copy image.');
      ctx.drawImage(bmp, 0, 0);
      const pngBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to convert signed copy image.'))),
          'image/png',
        );
      });
      image = await pdf.embedPng(new Uint8Array(await pngBlob.arrayBuffer()));
    }
    const dims = image.scale(1);
    const page = pdf.addPage([dims.width, dims.height]);
    page.drawImage(image, { x: 0, y: 0, width: dims.width, height: dims.height });
    return bytesToBase64(await pdf.save());
  }

  throw new Error('Signed copy must be a PDF or image file.');
}

/** Download the signed copy uploaded at post time; optionally stamp APPROVED + COPY. */
export async function downloadMemoSignatureCopy(
  storagePath: string,
  downloadName: string,
  options: { stamp?: 'posted' | 'none' } = {},
): Promise<void> {
  const path = storagePath.trim();
  if (!path) throw new Error('Signed copy is not available for this memorandum.');

  const db = getDbSupabase('forms');
  const { data, error } = await db.storage.from('memo-signatures').download(path);
  if (error || !data) {
    throw new Error(error?.message || 'Failed to download signed copy.');
  }

  const name = downloadName.trim() || 'signed-memorandum.pdf';

  if (options.stamp === 'posted') {
    const { stampMemoPdfBase64 } = await import('./memoDocumentStamps');
    let pdfB64 = await signedCopyBlobToPdfBase64(data, name);
    pdfB64 = await stampMemoPdfBase64(pdfB64, 'posted');
    const binary = atob(pdfB64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const baseName = name.replace(/\.pdf$/i, '');
    triggerBrowserDownload(new Blob([bytes], { type: 'application/pdf' }), `${baseName} COPY.pdf`);
    return;
  }

  triggerBrowserDownload(data, name);
}

export async function postMemo(
  id: string,
  signatureFileName: string,
  signatureStoragePath: string,
  postedBy?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('post_memo', {
    p_id: id,
    p_signature_file_name: signatureFileName,
    p_signature_storage_path: signatureStoragePath,
    p_posted_by: postedBy?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withMemoSlug(data as ApplicationSubmission);
}

export async function cancelMemo(
  id: string,
  cancelledBy?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('cancel_memo', {
    p_id: id,
    p_cancelled_by: cancelledBy?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return withMemoSlug(data as ApplicationSubmission);
}

export function isMemoLocked(status: string | undefined | null): boolean {
  return status === 'posted' || status === 'cancelled';
}

export type MemoAiStyleReference = {
  id: string;
  ref_no: string;
  subject: string;
  date: string;
  body_plain: string;
};

function tokenizeForMemoSimilarity(text: string): Set<string> {
  const tokens = text
    .toLowerCase()
    .replace(/[^a-z0-9*\s/-]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3);
  return new Set(tokens);
}

function scoreMemoSimilarity(hint: string, subject: string, bodyPlain: string): number {
  const hintTokens = tokenizeForMemoSimilarity(hint);
  if (hintTokens.size === 0) return 0;
  const subjectTokens = tokenizeForMemoSimilarity(subject);
  const bodyTokens = tokenizeForMemoSimilarity(bodyPlain.slice(0, 2000));
  let score = 0;
  for (const token of hintTokens) {
    if (subjectTokens.has(token)) score += 3;
    else if (bodyTokens.has(token)) score += 1;
  }
  return score;
}

/**
 * Recent posted memoranda for AI style/structure reference (scored by notes overlap).
 */
export async function fetchPostedMemosForAiStyle(options?: {
  notesHint?: string;
  excludeId?: string;
  /** Candidates returned to the UI (default 8). */
  candidateLimit?: number;
}): Promise<MemoAiStyleReference[]> {
  const candidateLimit = Math.min(12, Math.max(3, options?.candidateLimit ?? 8));
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from(TABLE)
    .select('id,application_no,status,form_data,submitted_at')
    .eq('status', 'posted')
    .order('submitted_at', { ascending: false })
    .limit(40);
  if (error) throw new Error(formatSupabaseError(error));

  const excludeId = options?.excludeId?.trim() || '';
  const hint = String(options?.notesHint ?? '').trim();

  const mapped: Array<MemoAiStyleReference & { score: number; submitted_at: string }> = [];
  for (const raw of data ?? []) {
    const row = raw as {
      id?: string;
      application_no?: string;
      form_data?: unknown;
      submitted_at?: string;
    };
    const id = String(row.id ?? '');
    if (!id || (excludeId && id === excludeId)) continue;
    const form = normalizeInternalMemoFormData(row.form_data);
    const body_plain = memoBodyPlainText(form.body).slice(0, 1800).trim();
    const subject = form.subject.trim() || String(row.application_no ?? '').trim();
    if (!subject && !body_plain) continue;
    mapped.push({
      id,
      ref_no: String(row.application_no ?? form.ref_no ?? '').trim(),
      subject,
      date: form.date.trim(),
      body_plain,
      score: scoreMemoSimilarity(hint, subject, body_plain),
      submitted_at: String(row.submitted_at ?? ''),
    });
  }

  mapped.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return b.submitted_at.localeCompare(a.submitted_at);
  });

  return mapped.slice(0, candidateLimit).map(({ score: _s, submitted_at: _t, ...rest }) => rest);
}
